// The fix a cleanup issue is asking for, declared by the detector that
// raised it.
//
// Browser-safe on purpose (no db imports): the in-record Cleanup card, the
// Data Needed card and the issue store all read this one shape, the same
// way detector-slugs.ts is shared.
//
// WHY THIS EXISTS (Grant, 2026-09-15). The Cleanup pane picked its action
// buttons from hand-written lists — 13 detector slugs special-cased in the
// front-end dispatch, ~24 in the backend, 16 bespoke button blocks — one
// case per detector, for 84 detectors. Any detector that asked a question
// the lists did not recognise fell through to dispositions that change
// nothing on the record ("mark resolved", "not an issue", "snooze"), and
// the pane presented those as if they were the answer. Measured on
// production that morning: ~600 of ~1,065 open issues had no control that
// changed the record. The same shape had been patched one detector at a
// time on 2026-07-31, 2026-08-07 and 2026-09-10.
//
// So the detector now says, in data, what kind of change it wants, and the
// pane renders THAT — the button is derived from the declaration, never
// from the detector's name. A new detector that declares a fix gets a
// working button without anyone writing UI for it.
//
// A declaration is a statement about the change, not a recommendation to
// make it. `confidence` is the pill the person reads: "confident" when two
// independent facts agree, "check-first" when the machine has a name but
// not a second fact, "needs-you" when only a person can pick. A
// `check-first` move still offers the button — the human has the evidence
// in front of them and the move undoes in one click — it just says so.

/** The four record kinds POST /api/records/:kind/:id/move-household accepts. */
export type MovableKind =
  | "service_record"
  | "opportunity"
  | "transaction"
  | "rhythm_instance";

export const MOVABLE_KINDS: readonly MovableKind[] = [
  "service_record",
  "opportunity",
  "transaction",
  "rhythm_instance",
];

export function isMovableKind(v: unknown): v is MovableKind {
  return typeof v === "string" && (MOVABLE_KINDS as readonly string[]).includes(v);
}

export type CleanupFixConfidence = "confident" | "check-first" | "needs-you";

/**
 * Which record kinds carry a workflow record's own fields — the two the
 * `type-it-in` editors write to.
 */
export type TypeItInRecordKind = "opportunity" | "transaction";

/**
 * The editors a `type-it-in` fix may name.
 *
 * Deliberately a CLOSED vocabulary rather than a description of a form.
 * A declaration that carried field names, labels and input types would be
 * a second place the OS states what a "Product metric" is called — and
 * the labels are already company-specific and already live in
 * shared/deal-value/metric-label.ts. So the detector names the editor and
 * the card mounts the real one, exactly as the record page does. Two
 * editors today:
 *
 *   deal-numbers   — expected close date, product metric, Year-1 revenue
 *                    (the Data Needed NumbersEditor, unchanged)
 *   record-people  — who this record is for (the record page's
 *                    RecordPeopleControl, unchanged)
 */
export type CleanupTypeItInEditor = "deal-numbers" | "record-people" | "fields";

const TYPE_IT_IN_EDITORS: readonly CleanupTypeItInEditor[] = [
  "deal-numbers",
  "record-people",
  "fields",
];

// ── "fields": the detector names the fields itself (2026-09-30) ──────
//
// Grant, on the NMLS card: "not only is the copy unclear on exactly what
// data is needed and why, but there is no way for me to give the data
// right here in this screen." The two named editors above cover two
// records' worth of fields; every other "type it in" ask (a loan's
// call-report classifications, a policy's expiration, a sale price) had
// to be wired by hand or fell through to a "verify" that wrote nothing.
//
// So a detector may now declare the FIELDS it is asking for — each with
// its plain label, a one-sentence hint, what is on file now, the kind of
// input, the allowed answers — and the WRITER that saves them: one of a
// closed list of record endpoints the card already knows how to call
// (shared/cleanup/answer-writers.ts). The card renders the declaration;
// the endpoint keeps its own validation, company-scope gate and audit.
// Nothing here is a second write path.
//
// The labels are the detector's to state because they are the ASK, not a
// second home for the field's definition: the option lists come from the
// same shared constants the record page renders (LIEN_STATUS_OPTIONS and
// friends), so the two cannot drift.

/** The kinds of control a declared field may ask for. Money is
 *  deliberately absent until a detector needs it — every kind here has a
 *  wire format the writer registry states (see encodeAnswer). */
export type CleanupFieldInput = "select" | "yesno" | "date" | "text" | "email" | "phone";

const FIELD_INPUTS: readonly CleanupFieldInput[] = ["select", "yesno", "date", "text", "email", "phone"];

export interface CleanupAskedField {
  /** The body key the writer takes — the record's own column name. */
  key: string;
  /** Plain words, the way a teammate would say it: "Lien status". */
  label: string;
  /** One sentence a new hire needs; shown behind a "?" on the card. */
  hint?: string;
  /** What is on file now. null = not answered. yesno carries 0/1. */
  current: string | number | null;
  input: CleanupFieldInput;
  /** select only: the allowed answers, from the shared option list. */
  options?: readonly { value: string; label: string }[];
}

/** The record endpoints a declared field may be saved through. Closed on
 *  purpose: a writer named here has a registry entry with its method and
 *  path (answer-writers.ts) and a guard test that fails when the two
 *  lists drift. */
export type CleanupFieldWriter = "mortgage-loan-details" | "contact";

export const FIELD_WRITERS: readonly CleanupFieldWriter[] = ["mortgage-loan-details", "contact"];

/** A second answer on a typed-in card that is not "type a value"
 *  (2026-10-03): "It's all still right" (verify) or "Archive it". Each is
 *  an existing decide verb; the decide route pins every id to the issue's
 *  own records. */
export interface CleanupFieldsAlternative {
  label: string;
  consequence: string;
  verb: "verify" | "archive";
  params?: Record<string, string>;
  reasonText?: string;
  done: string;
}

function parseFieldsAlternative(raw: unknown): CleanupFieldsAlternative | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.verb !== "verify" && r.verb !== "archive") return null;
  if (typeof r.label !== "string" || !r.label.trim()) return null;
  if (typeof r.consequence !== "string" || typeof r.done !== "string") return null;
  const params =
    r.params && typeof r.params === "object"
      ? Object.fromEntries(
          Object.entries(r.params as Record<string, unknown>).filter(
            (e): e is [string, string] => typeof e[1] === "string",
          ),
        )
      : undefined;
  return {
    label: r.label,
    consequence: r.consequence,
    verb: r.verb,
    ...(params && Object.keys(params).length > 0 ? { params } : {}),
    ...(typeof r.reasonText === "string" && r.reasonText.trim() ? { reasonText: r.reasonText } : {}),
    done: r.done,
  };
}

function parseAskedField(raw: unknown): CleanupAskedField | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.key !== "string" || !r.key.trim()) return null;
  if (typeof r.label !== "string" || !r.label.trim()) return null;
  if (!(FIELD_INPUTS as readonly unknown[]).includes(r.input)) return null;
  const input = r.input as CleanupFieldInput;
  const current =
    typeof r.current === "string" || typeof r.current === "number" ? r.current : null;
  const options = Array.isArray(r.options)
    ? r.options
        .map((o) => (o && typeof o === "object" ? (o as Record<string, unknown>) : null))
        .filter(
          (o): o is Record<string, unknown> =>
            o !== null && typeof o.value === "string" && typeof o.label === "string",
        )
        .map((o) => ({ value: o.value as string, label: o.label as string }))
    : [];
  // A choice with nothing to choose from is not a control.
  if (input === "select" && options.length === 0) return null;
  return {
    key: r.key,
    label: r.label,
    ...(typeof r.hint === "string" && r.hint.trim() ? { hint: r.hint } : {}),
    current,
    input,
    ...(input === "select" ? { options } : {}),
  };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function parseAskDate(
  raw: unknown,
): { askDate: { label: string; current: string | null; optional?: true } } | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.label !== "string" || r.label.trim() === "") return null;
  const current = typeof r.current === "string" && ISO_DAY.test(r.current) ? r.current : null;
  return { askDate: { label: r.label, current, ...(r.optional === true ? { optional: true as const } : {}) } };
}

const CONFIDENCES: readonly CleanupFixConfidence[] = ["confident", "check-first", "needs-you"];

/** The one box on a `fill-one` card. */
export interface CleanupFillInput {
  type: "date" | "money" | "text" | "select" | "member" | "advisor";
  label: string;
  hint?: string;
  /** The verify param the decide route reads (e.g. "expiration_date"). */
  paramKey: string;
  /** What the record says now, shown as "On file: …"; null = nothing. */
  current: string | null;
  placeholder?: string;
  /** select only. */
  options?: readonly { value: string; label: string }[];
  /** text only: the shortest answer the server accepts. */
  minLength?: number;
  /** The box may be left empty (the answer is then "it's right as is"). */
  optional?: boolean;
  /** select only: a note box shown when this value is picked. */
  otherNote?: { whenValue: string; label: string; paramKey: string };
  /** A tick box sent as `paramKey: true` when ticked. */
  checkbox?: { label: string; paramKey: string };
  /** advisor only: the company whose own advisors the picker lists
   *  (shared/db/company-advisors.ts; the server checks the same rule). */
  company?: string;
}

/** A "we don't know" answer on a `fill-one` card: a fixed verb and fixed
 *  params, declared by the detector, checked by the decide route. */
export interface CleanupFillEscape {
  label: string;
  verb: "keep-current";
  params?: Record<string, string>;
  consequence: string;
}

const FILL_INPUT_TYPES = ["date", "money", "text", "select", "member", "advisor"] as const;

function parseFillInput(raw: unknown): CleanupFillInput | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!(FILL_INPUT_TYPES as readonly unknown[]).includes(r.type)) return null;
  if (typeof r.label !== "string" || !r.label.trim()) return null;
  if (typeof r.paramKey !== "string" || !/^[a-z_]+$/.test(r.paramKey)) return null;
  const type = r.type as CleanupFillInput["type"];
  const options =
    type === "select" && Array.isArray(r.options)
      ? r.options
          .map((o) => (o && typeof o === "object" ? (o as Record<string, unknown>) : null))
          .filter(
            (o): o is Record<string, unknown> =>
              o !== null && typeof o.value === "string" && typeof o.label === "string",
          )
          .map((o) => ({ value: o.value as string, label: o.label as string }))
      : [];
  if (type === "select" && options.length === 0) return null;
  const note = r.otherNote && typeof r.otherNote === "object" ? (r.otherNote as Record<string, unknown>) : null;
  const box = r.checkbox && typeof r.checkbox === "object" ? (r.checkbox as Record<string, unknown>) : null;
  return {
    type,
    label: r.label,
    paramKey: r.paramKey,
    current: typeof r.current === "string" && r.current.trim() ? r.current : null,
    ...(typeof r.hint === "string" && r.hint.trim() ? { hint: r.hint } : {}),
    ...(typeof r.placeholder === "string" && r.placeholder.trim() ? { placeholder: r.placeholder } : {}),
    ...(type === "select" ? { options } : {}),
    ...(typeof r.minLength === "number" && r.minLength > 0 ? { minLength: r.minLength } : {}),
    ...(r.optional === true ? { optional: true } : {}),
    ...(note &&
    typeof note.whenValue === "string" &&
    typeof note.label === "string" &&
    typeof note.paramKey === "string" &&
    /^[a-z_]+$/.test(note.paramKey)
      ? { otherNote: { whenValue: note.whenValue, label: note.label, paramKey: note.paramKey } }
      : {}),
    ...(type === "advisor" && typeof r.company === "string" && /^[a-z]+$/.test(r.company)
      ? { company: r.company }
      : {}),
    ...(box && typeof box.label === "string" && typeof box.paramKey === "string" && /^[a-z_]+$/.test(box.paramKey)
      ? { checkbox: { label: box.label, paramKey: box.paramKey } }
      : {}),
  };
}

function parseFillEscape(raw: unknown): CleanupFillEscape | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.verb !== "keep-current") return null;
  if (typeof r.label !== "string" || !r.label.trim()) return null;
  if (typeof r.consequence !== "string" || !r.consequence.trim()) return null;
  const params: Record<string, string> = {};
  if (r.params && typeof r.params === "object") {
    for (const [k, v] of Object.entries(r.params as Record<string, unknown>)) {
      if (typeof v === "string") params[k] = v;
    }
  }
  return {
    label: r.label,
    verb: "keep-current",
    ...(Object.keys(params).length > 0 ? { params } : {}),
    consequence: r.consequence,
  };
}

/** One answer a `pick-one` card offers. The consequence is the plain
 *  sentence under the button: what recording THIS answer does, so the
 *  person never has to guess what a button will change. */
export interface CleanupPickOption {
  value: string;
  label: string;
  consequence: string;
  /** The answer needs a short reason before it can be recorded ("still
   *  pending, and here's why"). The server enforces it, not only the card. */
  needsNote?: boolean;
  /** The answer needs a date before it can be recorded ("It funded on
   *  ___"). `current` is what the record already says, offered as the
   *  starting value. The server checks the date, not only the card. */
  askDate?: {
    label: string;
    current: string | null;
    /** The date may be left blank ("Start date, if you know it"). A date
     *  that IS given is still checked. */
    optional?: true;
  };
  /** Recording THIS answer changes a record (the server does it, from
   *  code keyed on the detector, never from anything the card sends).
   *  The card uses it only to tell the truth under the button. */
  changesRecord?: boolean;
}

/** The verbs a `choose-answer` card may send. Each is an existing decide
 *  verb whose branch already validates, writes and returns its undo; the
 *  card adds no new way to change a record. */
export const CHOOSE_ANSWER_VERBS = [
  "retag-as-vendor",
  "keep-current",
  "dismiss",
  "verify",
  "archive",
  "recase",
  // A source conflict's "use the proposed value" (keep-current is its
  // other answer), and the trust re-attach's one-click resolve. Both are
  // existing decide verbs with their own server-side checks (2026-10-03).
  "accept-proposed",
  "resolve-trust-reattach",
  // "I saw it, nothing on the record changes" (PR B, 2026-10-04): the
  // carrier-switch and "seen it" answers on the cancellation notice.
  "acknowledge",
  // The derived client-status row (record-without-relationship, PR B).
  "create-relationship-row",
  // Not a decide verb: the card opens the existing find-and-merge screen
  // for the issue's one household. Nothing is sent until that screen's
  // own merge lands (and it resolves the issue then).
  "find-household",
] as const;
export type CleanupChooseAnswerVerb = (typeof CHOOSE_ANSWER_VERBS)[number];

/** One answer on a `choose-answer` card: the button, the one line under
 *  it saying what pressing it does, and exactly what it sends. */
export interface CleanupChooseAnswer {
  label: string;
  consequence: string;
  verb: CleanupChooseAnswerVerb;
  /** Sent as-is. The decide route pins every id to the issue's own
   *  records, so a declared id is a convenience, never an authority. */
  params?: Record<string, string>;
  /** The audit reason the side panel sends for this answer. */
  reasonText?: string;
  /** The answer the detector recommends; it leads and is the primary
   *  button. At most one. */
  recommended?: boolean;
  /** The toast after it lands ("Tagged as a vendor."). */
  done: string;
  /** The answer needs the person's own reason before it is recorded
   *  ("not their home, it's a rental"). The decide route refuses the
   *  answer's verb on this item without one, not only the card. */
  needsNote?: boolean;
}

/** An internal route that does the real work the answer is about. Always
 *  an app path ("/admin/commissions"), never a URL: the card never links
 *  off the OS on a detector's say-so. */
export interface CleanupPickLink {
  label: string;
  path: string;
}

/** A family an account's title could belong to, as the matcher found it. */
export interface CleanupSplitFamily {
  householdId: string;
  /** "Robert & Maria Fernandez · Gilbert". */
  label: string;
}

/**
 * One account in a holding household that is being split (2026-10-04).
 * Everything here is what the detector READ; the decide route re-reads the
 * account and its household before anything moves, so none of it is an
 * authority. `suggestion` is set only under Grant's rule: exactly one real
 * household matches strongly, or no family exists and the title is a
 * person's own account (then "its own new household").
 */
export interface CleanupSplitAccount {
  portfolioId: string;
  serviceRecordId: string;
  /** The custodian's registration title, as on file. */
  title: string;
  lastFour: string | null;
  valueCents: number;
  /** "Trust", "IRA", "Joint", when the title says; null otherwise. */
  kindLabel: string | null;
  /** The owner names read from the title ("Robert Fernandez"). */
  names: readonly string[];
  isTrust: boolean;
  /** Families the title could belong to, strongest first (0 to 3). */
  families: readonly CleanupSplitFamily[];
  suggestion: { to: "family"; householdId: string } | { to: "new" } | null;
  /** What "Its own new household" would be called. Null when the title
   *  names nobody the OS can name a household after. */
  newHouseholdName: string | null;
  /** The person named on this account, when they are in the holding
   *  household: the card offers to bring them along (Grant, 2026-10-04). */
  person: { contactId: string; name: string } | null;
  /** One plain sentence: how we read the title and what matched. */
  read: string;
}

/** The answers a split card sends, one per account. */
export type SplitAccountAnswer =
  | { portfolioId: string; to: "family"; householdId: string; bringPerson?: boolean }
  | { portfolioId: string; to: "new"; zip?: string; bringPerson?: boolean }
  | { portfolioId: string; to: "leave" };

/** The most accounts one card lists. The largest known holding household
 *  (the Fernandez import) holds 13. */
export const SPLIT_MAX_ACCOUNTS = 40;

/** The existing pickers a card can open (2026-10-03, step 5). Each is
 *  the screen the side panel or another page already uses; the card only
 *  opens it from the front. */
export const OPEN_PICKERS = [
  "move-contact",
  "link-realty",
  "create-policy",
  "stub-family",
  "link-fee-name",
] as const;
export type CleanupOpenPicker = (typeof OPEN_PICKERS)[number];

export type CleanupFix =
  | {
      /**
       * A holding household from the Financial import with several
       * accounts whose titles name different owners (2026-10-04, the
       * "Fernandez" case). Each account gets its own answer: a family,
       * its own new household, or leave it here for now. One button moves
       * every answered account as one bulk action, with one Undo. The
       * holding household is the issue's one household ref.
       */
      kind: "split-accounts";
      title: string;
      holdingHouseholdId: string;
      accounts: readonly CleanupSplitAccount[];
      /** Accounts beyond the batch shown (SPLIT_MAX_ACCOUNTS). They appear
       *  once these have moved; the card says how many wait. */
      moreAccounts?: number;
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * The answer is a choice made in a picker the OS already has
       * (2026-10-03). The card leads with it; the host owns the screen
       * (move a contact, link a Realty record, create a policy, merge a
       * placeholder into one of its families) and resolves the item when
       * that screen's own action lands. "link-fee-name" is a household
       * search on the card itself, saved through Fee Review's link.
       */
      kind: "open-picker";
      title: string;
      picker: CleanupOpenPicker;
      /** The button ("Find their household"). */
      label: string;
      /** The line under it: what the screen does. */
      consequence: string;
    }
  | {
      /**
       * A win nothing was created from (2026-10-03): something marked the
       * opportunity Won outside the OS, so no Processing record exists.
       * The card reopens the stray win (POST /api/cleanup/issues/:id/
       * reopen-stray-win, which refuses a win with a record behind it)
       * and runs the ordinary Mark Won or Mark Lost dialog on it. One row
       * per opportunity; the ids must be the issue's own.
       */
      kind: "rewin";
      title: string;
      opportunities: readonly { id: string; opportunityType: string }[];
    }
  | {
      /**
       * A record missing required documents (2026-10-02). The card lists
       * each one live (GET /api/cleanup/issues/:id/missing-documents), with
       * an upload filed under that document's type on the record, and "Not
       * needed because…" with a required reason (Grant: immediate, with a
       * reason). The record is the issue's own service_record ref.
       */
      kind: "missing-documents";
      title: string;
    }
  | {
      /**
       * A document past its retention period (2026-10-02). The card
       * mounts the same keep / hold / destroy panel the side panel uses
       * (RetentionDispositionPanel): admin-only on the server, destroy
       * behind its typed confirmation. The document is the issue's own
       * affected record.
       */
      kind: "retention";
      title: string;
    }
  | {
      /**
       * A placeholder household whose family nobody has named yet
       * (2026-10-02). The card opens the existing search-and-merge screen
       * from the card: find the family, review every field, merge. The
       * household is the issue's only household ref.
       */
      kind: "find-household";
      title: string;
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * Two households that might be one family (2026-10-02, the
       * household version of "same-person"). The card shows them side by
       * side (GET /api/cleanup/issues/:id/households) and offers the two
       * real answers: review every field and merge (the existing
       * household merge screen, opened from the card with `survivorId`
       * pre-picked and one click to switch), or "different households",
       * a dismiss with that reason. The households are the issue's own
       * affected records. `likelySame` decides which answer leads.
       */
      kind: "same-household";
      title: string;
      /** "import-stub": the first household is an import placeholder and
       *  the second the family its record points at. The card then offers
       *  the placeholder's three answers (same person under another
       *  surname / different people, one household / not the same family)
       *  with none pre-picked, because the data does not settle it. */
      variant?: "import-stub";
      likelySame: boolean;
      /** The household the detector would keep; must be one of the issue's. */
      survivorId: string | null;
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * A question with two to four real answers, each a different change
       * (2026-10-02, Data Needed overhaul): "Is this a business, not a
       * person?", "Two people in one contact: split or archive?". Every
       * answer is a button with one line under it saying what it does,
       * and sends exactly what the side panel's button for it sends.
       */
      kind: "choose-answer";
      title: string;
      answers: readonly CleanupChooseAnswer[];
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * Two or more contacts that might be one person (2026-10-02). The
       * card shows them side by side (GET /api/cleanup/issues/:id/people)
       * and offers the two real answers: review every field and merge
       * (the existing field-by-field merge screen, opened from the card,
       * which picks the most complete record to keep so protected fields
       * are never lost), or "different people", a dismiss with that
       * reason. The contacts are the issue's own affected records.
       * `likelySame` decides which answer leads.
       */
      kind: "same-person";
      title: string;
      matchType: "email" | "phone" | "name_zip";
      /** What they share, in words ("jon@x.com", "(480) 555-0142"). */
      matchValue: string | null;
      likelySame: boolean;
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * One value only a person can supply, answered on the card
       * (2026-10-02, step 3 of the Data Needed overhaul). The card sends
       * EXACTLY what the decide rail's input for this detector sends:
       * `verify` with the issue's own recommendation params plus
       * `input.paramKey`, or one of the `escapes` (a "we don't know"
       * answer). Nothing about how the answer is saved changes; the
       * decide route's branch for the detector already validates it,
       * writes it and hands back its undo.
       */
      kind: "fill-one";
      title: string;
      input: CleanupFillInput;
      /** The button, with "{value}" standing for what was entered
       *  ("Set expiration to {value}"). */
      applyLabel: string;
      /** Only for an optional input: the button when the box is empty
       *  ("Verified: the date on file is right"). */
      blankApplyLabel?: string;
      escapes: readonly CleanupFillEscape[];
      /** The household whose members a "member" input lists. */
      householdId: string | null;
      /** The decide verb the answer is sent with: "verify" for a value,
       *  "match" for an advisor assignment (the route's assign branch). */
      verb?: "verify" | "match";
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * A policy the carrier has that never made it into the OS (an
       * import row nobody could place). The card asks whether it is real;
       * "yes" means find the household and add it with the household
       * page's own Add Policy form, pre-filled from the carrier row; "no"
       * is a dismiss with a reason. Nothing is pre-picked: the importer
       * could not tell whose policy it is, so any household it offered
       * would be a guess (import-quarantine.ts, DECISION entityKind).
       * `searchHint` seeds the household search (the insured's name).
       */
      kind: "add-policy";
      title: string;
      prefill: { carrier: string | null; policyNumber: string | null; insuredName: string | null };
      searchHint: string | null;
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * A DECISION about something the OS is not the system of record
       * for (a custodian's fee, a commission disbursement, a QuickBooks
       * account, a mailbox subscription). There is nothing on the record
       * to change, so the card cannot pretend to fix anything; what it
       * can do is say exactly what is being asked, take the answer with a
       * reason, record who answered what, and keep the real action one
       * labelled click away (`link`). Source-of-truth doctrine in
       * CLAUDE.md: the OS never competes with the authoritative system.
       *
       * Two to five options (five since 2026-10-23, so a paid-short money
       * card can offer "Dispute it" beside its four closings); the server
       * checks the answer against THIS list, so a stale or forged answer
       * is a clean 422.
       */
      kind: "pick-one";
      title: string;
      options: readonly CleanupPickOption[];
      link?: CleanupPickLink;
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * Several records describe one real thing. Keep one, retire the
       * rest.
       *
       * `keeperId` is the detector's reading, not its decision: the card
       * pre-selects it and the person can pick any other row. Null when
       * only a person can say — two of our policies carrying the
       * carrier's number is exactly that, and pre-selecting one there
       * would be a guess wearing a default's clothes.
       *
       * Retiring is an archive, never a delete: every retired record is
       * restorable, and the card says so before the click.
       */
      kind: "keep-one";
      title: string;
      candidates: readonly { kind: MovableKind; id: string }[];
      keeperId: string | null;
      /** An honest "they're all real" answer, when the cluster may be
       *  separate things (two ACA plans). Keeps every record and closes the
       *  item with the person's reason (the decide route's dismiss). */
      keepAll?: { label: string; consequence: string };
      /** How the others are retired. "archive" (the default) archives
       *  each; "merge" merges them into the one kept through the decide
       *  route's merge (their history folds in), and asks for a reason
       *  code when the item's tier needs one. */
      retire?: "archive" | "merge";
      confidence: CleanupFixConfidence;
    }
  | {
      kind: "move-record";
      /** Short row title, e.g. "Loan on the wrong household". */
      title: string;
      /** The record that moves. Its labels come from the issue's affected records. */
      record: { kind: MovableKind; id: string };
      /** Where it sits now. Null only when the detector could not tell. */
      fromHouseholdId: string | null;
      /** Where the detector believes it belongs. Null means the person
       *  has to find it (the pane opens on search). */
      toHouseholdId: string | null;
      confidence: CleanupFixConfidence;
    }
  | {
      /**
       * The detector named the fields itself (see CleanupAskedField).
       * `record` is the record the issue is ABOUT (what the card links
       * to); `writerId` is the id the writer endpoint takes, which may be
       * a subtype row (a loan's mortgage_loans id, not its service
       * record).
       */
      kind: "type-it-in";
      title: string;
      editor: "fields";
      record: { kind: string; id: string };
      householdId: string | null;
      confidence: CleanupFixConfidence;
      writer: CleanupFieldWriter;
      writerId: string;
      fields: readonly CleanupAskedField[];
      /** Answers besides typing a value (at most two). */
      alternatives?: readonly CleanupFieldsAlternative[];
      /** The detector cannot re-check this record on save (it has no
       *  per-record scope), so a successful save closes the item, saying
       *  so. Without it the item clears when the record's own check
       *  passes. */
      closeOnSave?: true;
    }
  | {
      /**
       * A value is missing and only a person can supply it. The card
       * mounts the named editor against the live record and saves through
       * the same endpoint the record page uses.
       *
       * There is no `keeperId`-style suggestion here on purpose: the
       * whole reason these issues exist is that the OS does not know the
       * answer (Minimum Viable Typing category 6). For the same reason
       * `confidence` is always "needs-you" in practice — the field is
       * here so every shape reads the same way, not because a machine
       * could ever be "confident" about a number only a person knows.
       */
      kind: "type-it-in";
      title: string;
      editor: "deal-numbers" | "record-people";
      record: { kind: TypeItInRecordKind; id: string };
      /** The household the record sits on — the people picker reads its
       *  members from it, so a `record-people` fix without one cannot be
       *  answered and is rejected below. */
      householdId: string | null;
      confidence: CleanupFixConfidence;
    }
  | {
      /** Nothing on the record to change; the person confirms they saw it. */
      kind: "acknowledge";
      title: string;
    }
  | {
      /**
       * The answer lives on one exact page the card cannot hold: a file to
       * upload (Gusto's payroll report), several differences that each
       * need their own answer, a replacement payment with its amount and
       * reference (build step C1). The card says what to do and its one
       * button opens that place. Nothing is recorded from the card; the
       * item clears itself when the page's own action lands, so there is
       * no "Seen it" that could hide work still owed.
       */
      kind: "open-page";
      title: string;
      /** What the person does once there, in one sentence. */
      what: string;
      link: CleanupPickLink;
    };

/**
 * Validate a fix read back from JSON. Defensive by design: fix_json is
 * written by detectors across many PRs, and a malformed declaration must
 * degrade to "no declared fix" (the pane falls back to what it can infer)
 * rather than to a crash on every card.
 */
function parseSplitAccount(raw: unknown): CleanupSplitAccount | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
  const portfolioId = str(a.portfolioId);
  const serviceRecordId = str(a.serviceRecordId);
  const title = str(a.title);
  if (!portfolioId || !serviceRecordId || !title) return null;
  const valueCents = typeof a.valueCents === "number" && Number.isFinite(a.valueCents) ? Math.round(a.valueCents) : 0;
  const families: CleanupSplitFamily[] = [];
  for (const f of Array.isArray(a.families) ? a.families : []) {
    if (!f || typeof f !== "object") continue;
    const fr = f as Record<string, unknown>;
    const householdId = str(fr.householdId);
    const label = str(fr.label);
    if (!householdId || !label || families.some((x) => x.householdId === householdId)) continue;
    families.push({ householdId, label });
  }
  const sug = a.suggestion && typeof a.suggestion === "object" ? (a.suggestion as Record<string, unknown>) : null;
  // A suggested family the row does not list is a detector bug; drop it
  // rather than pre-pick a household the card cannot name.
  const suggestion: CleanupSplitAccount["suggestion"] =
    sug?.to === "family" && typeof sug.householdId === "string" && families.some((f) => f.householdId === sug.householdId)
      ? { to: "family", householdId: sug.householdId }
      : sug?.to === "new" && str(a.newHouseholdName)
        ? { to: "new" }
        : null;
  const p = a.person && typeof a.person === "object" ? (a.person as Record<string, unknown>) : null;
  const person = p && str(p.contactId) && str(p.name) ? { contactId: p.contactId as string, name: p.name as string } : null;
  return {
    portfolioId,
    serviceRecordId,
    title,
    lastFour: typeof a.lastFour === "string" && /^\d{4}$/.test(a.lastFour) ? a.lastFour : null,
    valueCents,
    kindLabel: str(a.kindLabel),
    names: (Array.isArray(a.names) ? a.names : []).filter((n): n is string => typeof n === "string" && n.trim() !== ""),
    isTrust: a.isTrust === true,
    families: families.slice(0, 3),
    suggestion,
    newHouseholdName: str(a.newHouseholdName),
    person,
    read: str(a.read) ?? "",
  };
}

/**
 * Read the per-account answers a split card sent. Shape only: whether an
 * account, a household or a person belongs to the item is the decide
 * route's question, against the database, never this one's. Returns null
 * for anything malformed, so the route answers 400 rather than guess.
 */
export function parseSplitAnswers(raw: unknown): SplitAccountAnswer[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > SPLIT_MAX_ACCOUNTS) return null;
  const out: SplitAccountAnswer[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") return null;
    const a = item as Record<string, unknown>;
    if (typeof a.portfolio_id !== "string" || !a.portfolio_id) return null;
    const portfolioId = a.portfolio_id;
    const bring = a.bring_person === true;
    if (a.to === "family") {
      if (typeof a.household_id !== "string" || !a.household_id) return null;
      out.push({ portfolioId, to: "family", householdId: a.household_id, ...(bring ? { bringPerson: true } : {}) });
    } else if (a.to === "new") {
      const zip = typeof a.zip === "string" ? a.zip.trim() : "";
      out.push({ portfolioId, to: "new", ...(zip ? { zip } : {}), ...(bring ? { bringPerson: true } : {}) });
    } else if (a.to === "leave") {
      out.push({ portfolioId, to: "leave" });
    } else {
      return null;
    }
  }
  if (new Set(out.map((a) => a.portfolioId)).size !== out.length) return null;
  return out;
}

/** A US zip, five digits. The optional zip on "its own new household". */
export function isFiveDigitZip(v: string): boolean {
  return /^\d{5}$/.test(v);
}

export function parseCleanupFix(raw: unknown): CleanupFix | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const title = typeof r.title === "string" && r.title.trim() ? r.title : null;
  if (!title) return null;
  if (r.kind === "acknowledge") return { kind: "acknowledge", title };
  if (r.kind === "open-page") {
    const rawLink = r.link && typeof r.link === "object" ? (r.link as Record<string, unknown>) : null;
    const what = typeof r.what === "string" && r.what.trim() ? r.what : null;
    // An app route only, the same rule as pick-one's link.
    if (
      !rawLink ||
      !what ||
      typeof rawLink.label !== "string" ||
      rawLink.label.trim() === "" ||
      typeof rawLink.path !== "string" ||
      !/^\/(?!\/)/.test(rawLink.path)
    ) {
      return null;
    }
    return { kind: "open-page", title, what, link: { label: rawLink.label, path: rawLink.path } };
  }
  const confidenceOf = (v: unknown): CleanupFixConfidence =>
    (CONFIDENCES as readonly unknown[]).includes(v)
      ? (v as CleanupFixConfidence)
      : "needs-you";
  if (r.kind === "missing-documents") {
    return { kind: "missing-documents", title };
  }
  if (r.kind === "split-accounts") {
    const holdingHouseholdId =
      typeof r.holdingHouseholdId === "string" && r.holdingHouseholdId ? r.holdingHouseholdId : null;
    if (!holdingHouseholdId) return null;
    const accounts = (Array.isArray(r.accounts) ? r.accounts : [])
      .map(parseSplitAccount)
      .filter((a): a is CleanupSplitAccount => a !== null);
    // Usually two or more accounts; one is allowed for a household already
    // part-split (the drawing's "One account is still here"). Two rows for
    // one account would be one answer under two names.
    if (accounts.length < 1 || accounts.length > SPLIT_MAX_ACCOUNTS) return null;
    if (new Set(accounts.map((a) => a.portfolioId)).size !== accounts.length) return null;
    const moreAccounts =
      typeof r.moreAccounts === "number" && Number.isInteger(r.moreAccounts) && r.moreAccounts > 0 ? r.moreAccounts : undefined;
    return {
      kind: "split-accounts",
      title,
      holdingHouseholdId,
      accounts,
      ...(moreAccounts ? { moreAccounts } : {}),
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind === "open-picker") {
    if (!(OPEN_PICKERS as readonly unknown[]).includes(r.picker)) return null;
    if (typeof r.label !== "string" || !r.label.trim() || typeof r.consequence !== "string") return null;
    return {
      kind: "open-picker",
      title,
      picker: r.picker as CleanupOpenPicker,
      label: r.label,
      consequence: r.consequence,
    };
  }
  if (r.kind === "rewin") {
    const opportunities = (Array.isArray(r.opportunities) ? r.opportunities : [])
      .map((o) => (o && typeof o === "object" ? (o as Record<string, unknown>) : null))
      .filter(
        (o): o is Record<string, unknown> =>
          o !== null && typeof o.id === "string" && o.id !== "" && typeof o.opportunityType === "string",
      )
      .map((o) => ({ id: o.id as string, opportunityType: o.opportunityType as string }));
    if (opportunities.length === 0) return null;
    return { kind: "rewin", title, opportunities };
  }
  if (r.kind === "retention") {
    return { kind: "retention", title };
  }
  if (r.kind === "find-household") {
    return { kind: "find-household", title, confidence: confidenceOf(r.confidence) };
  }
  if (r.kind === "same-household") {
    return {
      kind: "same-household",
      title,
      ...(r.variant === "import-stub" ? { variant: "import-stub" as const } : {}),
      likelySame: r.likelySame === true,
      survivorId: typeof r.survivorId === "string" && r.survivorId ? r.survivorId : null,
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind === "choose-answer") {
    const raw = Array.isArray(r.answers) ? r.answers : [];
    const answers: CleanupChooseAnswer[] = [];
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const a = item as Record<string, unknown>;
      if (typeof a.label !== "string" || !a.label.trim()) continue;
      if (typeof a.consequence !== "string" || !a.consequence.trim()) continue;
      if (typeof a.done !== "string" || !a.done.trim()) continue;
      if (!(CHOOSE_ANSWER_VERBS as readonly unknown[]).includes(a.verb)) continue;
      const params: Record<string, string> = {};
      if (a.params && typeof a.params === "object") {
        for (const [k, v] of Object.entries(a.params as Record<string, unknown>)) {
          if (typeof v === "string" && /^[a-z_]+$/.test(k)) params[k] = v;
        }
      }
      answers.push({
        label: a.label,
        consequence: a.consequence,
        verb: a.verb as CleanupChooseAnswerVerb,
        ...(Object.keys(params).length > 0 ? { params } : {}),
        ...(typeof a.reasonText === "string" && a.reasonText.trim() ? { reasonText: a.reasonText } : {}),
        ...(a.recommended === true ? { recommended: true } : {}),
        ...(a.needsNote === true ? { needsNote: true } : {}),
        done: a.done,
      });
    }
    // One answer is not a choice; two recommended answers is no
    // recommendation; two buttons sending the same verb would be one
    // answer under two names.
    if (answers.length < 2 || answers.length > 4) return null;
    if (answers.filter((a) => a.recommended).length > 1) return null;
    if (new Set(answers.map((a) => a.verb)).size !== answers.length) return null;
    return { kind: "choose-answer", title, answers, confidence: confidenceOf(r.confidence) };
  }
  if (r.kind === "same-person") {
    const mt = r.matchType;
    if (mt !== "email" && mt !== "phone" && mt !== "name_zip") return null;
    return {
      kind: "same-person",
      title,
      matchType: mt,
      matchValue: typeof r.matchValue === "string" && r.matchValue.trim() ? r.matchValue : null,
      likelySame: r.likelySame === true,
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind === "fill-one") {
    const input = parseFillInput(r.input);
    if (!input) return null;
    if (typeof r.applyLabel !== "string" || !r.applyLabel.includes("{value}")) return null;
    // A member picker lists one household's people; without the household
    // there is nothing to pick from.
    if (input.type === "member" && !(typeof r.householdId === "string" && r.householdId)) return null;
    // An advisor picker lists one company's advisors; with no company it
    // would have to fall back to the whole firm, which is the bug it fixes.
    if (input.type === "advisor" && !input.company) return null;
    return {
      kind: "fill-one",
      title,
      input,
      applyLabel: r.applyLabel,
      ...(typeof r.blankApplyLabel === "string" && r.blankApplyLabel.trim() && input.optional
        ? { blankApplyLabel: r.blankApplyLabel }
        : {}),
      escapes: (Array.isArray(r.escapes) ? r.escapes : [])
        .map(parseFillEscape)
        .filter((e): e is CleanupFillEscape => e !== null),
      householdId: typeof r.householdId === "string" && r.householdId ? r.householdId : null,
      ...(r.verb === "match" ? { verb: "match" as const } : {}),
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind === "add-policy") {
    const p = r.prefill && typeof r.prefill === "object" ? (r.prefill as Record<string, unknown>) : {};
    const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
    return {
      kind: "add-policy",
      title,
      prefill: { carrier: str(p.carrier), policyNumber: str(p.policyNumber), insuredName: str(p.insuredName) },
      searchHint: str(r.searchHint),
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind === "pick-one") {
    const rawOptions = Array.isArray(r.options) ? r.options : [];
    const options = rawOptions
      .map((o) => (o && typeof o === "object" ? (o as Record<string, unknown>) : null))
      .filter(
        (o): o is Record<string, unknown> =>
          o !== null &&
          typeof o.value === "string" &&
          o.value.trim() !== "" &&
          typeof o.label === "string" &&
          o.label.trim() !== "" &&
          typeof o.consequence === "string" &&
          o.consequence.trim() !== "",
      )
      .map((o) => ({
        value: o.value as string,
        label: o.label as string,
        consequence: o.consequence as string,
        ...(o.needsNote === true ? { needsNote: true } : {}),
        ...(parseAskDate(o.askDate) ?? {}),
        ...(o.changesRecord === true ? { changesRecord: true } : {}),
      }));
    // One option is not a choice, and a duplicate value would make the
    // recorded answer ambiguous.
    if (options.length < 2 || options.length > 5) return null;
    if (new Set(options.map((o) => o.value)).size !== options.length) return null;
    const rawLink = r.link && typeof r.link === "object" ? (r.link as Record<string, unknown>) : null;
    const link =
      rawLink &&
      typeof rawLink.label === "string" &&
      rawLink.label.trim() !== "" &&
      typeof rawLink.path === "string" &&
      // An app route only: starts with one slash, not "//host" (which a
      // browser reads as another site).
      /^\/(?!\/)/.test(rawLink.path)
        ? { label: rawLink.label, path: rawLink.path }
        : null;
    return {
      kind: "pick-one",
      title,
      options,
      ...(link ? { link } : {}),
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind === "keep-one") {
    const raw = Array.isArray(r.candidates) ? r.candidates : [];
    const candidates = raw
      .map((c) => (c && typeof c === "object" ? (c as Record<string, unknown>) : null))
      .filter((c): c is Record<string, unknown> => c !== null)
      .filter((c) => isMovableKind(c.kind) && typeof c.id === "string" && c.id.length > 0)
      .map((c) => ({ kind: c.kind as MovableKind, id: c.id as string }));
    // One record is not a cluster, and nothing can be "kept" out of it.
    if (candidates.length < 2) return null;
    const keeperRaw = typeof r.keeperId === "string" ? r.keeperId : null;
    // A keeper that is not one of the candidates is a detector bug; drop
    // it rather than pre-selecting a row the card cannot show.
    const keeperId = keeperRaw && candidates.some((c) => c.id === keeperRaw) ? keeperRaw : null;
    const ka = r.keepAll && typeof r.keepAll === "object" ? (r.keepAll as Record<string, unknown>) : null;
    const keepAll =
      ka && typeof ka.label === "string" && ka.label.trim() && typeof ka.consequence === "string"
        ? { label: ka.label, consequence: ka.consequence }
        : null;
    return {
      kind: "keep-one",
      title,
      candidates,
      keeperId,
      ...(keepAll ? { keepAll } : {}),
      ...(r.retire === "merge" ? { retire: "merge" as const } : {}),
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind === "type-it-in") {
    const editor = (TYPE_IT_IN_EDITORS as readonly unknown[]).includes(r.editor)
      ? (r.editor as CleanupTypeItInEditor)
      : null;
    if (!editor) return null;
    const rec = r.record as Record<string, unknown> | undefined;
    if (editor === "fields") {
      if (!rec || typeof rec.kind !== "string" || !rec.kind || typeof rec.id !== "string" || !rec.id) {
        return null;
      }
      const writer = (FIELD_WRITERS as readonly unknown[]).includes(r.writer)
        ? (r.writer as CleanupFieldWriter)
        : null;
      if (!writer || typeof r.writerId !== "string" || !r.writerId) return null;
      const fields = (Array.isArray(r.fields) ? r.fields : [])
        .map(parseAskedField)
        .filter((f): f is CleanupAskedField => f !== null);
      // No fields is not an ask; the pane falls back to the panel.
      if (fields.length === 0) return null;
      return {
        kind: "type-it-in",
        title,
        editor: "fields",
        record: { kind: rec.kind, id: rec.id },
        householdId:
          typeof r.householdId === "string" && r.householdId.length > 0 ? r.householdId : null,
        confidence: confidenceOf(r.confidence),
        writer,
        writerId: r.writerId,
        fields,
        ...(() => {
          const alternatives = (Array.isArray(r.alternatives) ? r.alternatives : [])
            .map(parseFieldsAlternative)
            .filter((a): a is CleanupFieldsAlternative => a !== null)
            .slice(0, 2);
          return alternatives.length > 0 ? { alternatives } : {};
        })(),
        ...(r.closeOnSave === true ? { closeOnSave: true as const } : {}),
      };
    }
    const kind =
      rec && (rec.kind === "opportunity" || rec.kind === "transaction")
        ? (rec.kind as TypeItInRecordKind)
        : null;
    if (!kind || !rec || typeof rec.id !== "string" || !rec.id) return null;
    const householdId =
      typeof r.householdId === "string" && r.householdId.length > 0 ? r.householdId : null;
    // The people editor lists the household's members to pick from, so
    // without a household there is nothing to pick. Reject rather than
    // render a picker with an empty list: the pane then falls back to the
    // research-and-decide panel, which still deep-links the record.
    if (editor === "record-people" && !householdId) return null;
    return {
      kind: "type-it-in",
      title,
      editor,
      record: { kind, id: rec.id },
      householdId,
      confidence: confidenceOf(r.confidence),
    };
  }
  if (r.kind !== "move-record") return null;
  const record = r.record as Record<string, unknown> | undefined;
  if (!record || !isMovableKind(record.kind) || typeof record.id !== "string" || !record.id) {
    return null;
  }
  const confidence = confidenceOf(r.confidence);
  const idOrNull = (v: unknown): string | null =>
    typeof v === "string" && v.length > 0 ? v : null;
  return {
    kind: "move-record",
    title,
    record: { kind: record.kind, id: record.id },
    fromHouseholdId: idOrNull(r.fromHouseholdId),
    toHouseholdId: idOrNull(r.toHouseholdId),
    confidence,
  };
}
